import React from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { Link } from 'react-router-dom';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import { User, Mail, Shield, Calendar, Settings } from 'lucide-react';

const Profile = () => {
  const { userData } = useAuth();

  if (!userData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p>Loading profile...</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'creater': return 'bg-purple-100 text-purple-700';
      case 'student': return 'bg-blue-100 text-blue-700';
      case 'admin': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleLabel = (role) => {
    if (role === 'creater') return 'Teacher';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">My Profile</h1>
          <p className="text-secondary-600 mt-1">View your account information</p>
        </div>
        <Link to={`/${userData.role === 'creater' ? 'teacher' : userData.role}/settings`}>
          <Button variant="outline" className="flex items-center">
            <Settings size={16} className="mr-2" />
            Settings
          </Button>
        </Link>
      </div>

      <Card>
        <Card.Content className="p-8">
          {/* Profile Header */}
          <div className="flex items-center space-x-6 mb-8 pb-8 border-b border-secondary-200">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-3xl font-bold">
              {userData.name ? userData.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-secondary-900 mb-2">{userData.name}</h2>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(userData.role)}`}>
                  {getRoleLabel(userData.role)}
                </span>
                {userData.createdAt && (
                  <span className="text-sm text-secondary-600">
                    Member since {formatDate(userData.createdAt)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Profile Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-secondary-700">
                <User size={16} className="mr-2" />
                Full Name
              </label>
              <div className="p-3 bg-secondary-50 rounded-lg border border-secondary-200">
                <p className="text-secondary-900">{userData.name || 'Not provided'}</p>
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-secondary-700">
                <Mail size={16} className="mr-2" />
                Email Address
              </label>
              <div className="p-3 bg-secondary-50 rounded-lg border border-secondary-200">
                <p className="text-secondary-900">{userData.email || 'Not provided'}</p>
              </div>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-secondary-700">
                <Shield size={16} className="mr-2" />
                Account Type
              </label>
              <div className="p-3 bg-secondary-50 rounded-lg border border-secondary-200">
                <p className="text-secondary-900">{getRoleLabel(userData.role)}</p>
              </div>
            </div>

            {/* Member Since */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-secondary-700">
                <Calendar size={16} className="mr-2" />
                Member Since
              </label>
              <div className="p-3 bg-secondary-50 rounded-lg border border-secondary-200">
                <p className="text-secondary-900">{formatDate(userData.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Info Message */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Note:</span> To update your password or other settings, please visit the Settings page.
            </p>
          </div>
        </Card.Content>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to={`/${userData.role === 'creater' ? 'teacher' : userData.role}/dashboard`}>
          <Card className="hover:shadow-medium transition-shadow cursor-pointer">
            <Card.Content className="p-4">
              <h3 className="font-semibold text-secondary-900 mb-1">Dashboard</h3>
              <p className="text-sm text-secondary-600">View your dashboard and statistics</p>
            </Card.Content>
          </Card>
        </Link>
        <Link to={`/${userData.role === 'creater' ? 'teacher' : userData.role}/settings`}>
          <Card className="hover:shadow-medium transition-shadow cursor-pointer">
            <Card.Content className="p-4">
              <h3 className="font-semibold text-secondary-900 mb-1">Settings</h3>
              <p className="text-sm text-secondary-600">Manage your account settings</p>
            </Card.Content>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default Profile;
